/**
 * Crypto utilities for the game engine
 * 
 * Provides cryptographic functions for secure randomness and verification.
 */

/**
 * Generate SHA-256 hash of a string
 * @param {string} message - Message to hash
 * @returns {Promise<string>} Hex-encoded hash
 */
export async function sha256(message) {
  // Use the Web Crypto API
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Generate a cryptographic proof for a game result
 * @param {string} seed - Seed used to generate the result
 * @param {Object} result - Game result
 * @returns {Promise<Object>} Proof object
 */
export async function generateProof(seed, result) {
  // For now, simple proof is just a hash of seed + result
  const resultString = JSON.stringify(result);
  const hash = await sha256(`${seed}:${resultString}`);
  
  return {
    hash,
    timestamp: Date.now(),
    version: '1.0'
  };
}

/**
 * Verify a cryptographic proof
 * @param {string} seed - Seed used to generate the result
 * @param {Object} result - Game result
 * @param {Object} proof - Proof to verify
 * @returns {Promise<boolean>} True if proof is valid
 */
export async function verifyProof(seed, result, proof) {
  const resultString = JSON.stringify(result);
  const expectedHash = await sha256(`${seed}:${resultString}`);
  
  return expectedHash === proof.hash;
}

/**
 * Generate a random UUID
 * @returns {string} UUID
 */
export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Generate a deterministic UUID from a seed
 * @param {string} seed - Seed
 * @returns {Promise<string>} UUID
 */
export async function generateDeterministicUUID(seed) {
  const hash = await sha256(seed);
  
  // Format as UUID
  return [
    hash.substring(0, 8),
    hash.substring(8, 12),
    '4' + hash.substring(13, 16),
    '8' + hash.substring(17, 20),
    hash.substring(20, 32)
  ].join('-');
}

/**
 * Generate a random string
 * @param {number} length - Length of string
 * @returns {string} Random string
 */
export function generateRandomString(length = 16) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  
  for (let i = 0; i < length; i++) {
    result += chars.charAt(randomValues[i] % chars.length);
  }
  
  return result;
}
